import { useState } from 'react';
import { Gift, ArrowRight, Check, MessageCircle, Smartphone, Landmark, Loader2, Clock, ShieldCheck } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { submitGiftCard } from '@/lib/firebase';
import { useBusiness } from '@/lib/business';
import { waLink } from '@/data/site';
import { formatPula } from '@/lib/money';
import { useReveal } from '@/lib/useReveal';

const TIERS = [300, 500, 1000, 1500];
type Step = 'form' | 'pay' | 'loading' | 'done';

export function GiftCards() {
  useReveal();
  const biz = useBusiness();
  const pay = biz.payment;

  const [amount, setAmount] = useState<number>(500);
  const [custom, setCustom] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [message, setMessage] = useState('');
  const [hp, setHp] = useState('');
  const [step, setStep] = useState<Step>('form');
  const [error, setError] = useState('');

  const finalAmount = custom ? Number(custom) : amount;
  const ref = (fromName || recipientName || 'your name').trim();

  function toPayment() {
    setError('');
    if (!(finalAmount > 0)) return setError('Please choose an amount.');
    if (!recipientName.trim()) return setError("Please add the recipient's name.");
    if (!fromName.trim()) return setError('Please add your name.');
    if (!buyerEmail.trim()) return setError('Please add your email.');
    if (!buyerPhone.trim()) return setError('A phone number is required so we can confirm your payment.');
    setStep('pay');
  }

  async function submit() {
    setError('');
    setStep('loading');
    const started = Date.now();
    try {
      await submitGiftCard({
        hp, amount: finalAmount,
        recipientName: recipientName.trim(),
        recipientEmail: recipientEmail.trim(),
        fromName: fromName.trim(),
        buyerEmail: buyerEmail.trim(),
        buyerPhone: buyerPhone.trim(),
        message: message.trim(),
      });
      const wait = Math.max(0, 1400 - (Date.now() - started));
      setTimeout(() => setStep('done'), wait);
    } catch (e) {
      setError(e instanceof Error ? `${e.message} You can also order on WhatsApp.` : 'Something went wrong.');
      setStep('pay');
    }
  }

  const waText = `Hi Grace Haven, I'd like to buy a ${formatPula(finalAmount)} gift card for ${recipientName || '(recipient)'}.`;

  const PayRow = ({ icon: Icon, label, lines }: { icon: typeof Smartphone; label: string; lines: string[] }) => (
    <div className="flex gap-3 border border-line bg-paper p-3.5">
      <Icon size={20} className="mt-0.5 shrink-0 text-gold-deep" strokeWidth={1.5} />
      <div>
        <p className="font-body text-xs font-semibold uppercase tracking-wide text-espresso">{label}</p>
        {lines.map((l, i) => <p key={i} className="font-body text-sm text-muted">{l}</p>)}
      </div>
    </div>
  );

  return (
    <>
      <Seo title="Gift Cards" description="Give the gift of calm — Grace Haven Spa gift cards in Gaborone. Choose an amount, pay, and we'll activate the voucher." />
      <PageHeader eyebrow="Give the gift of calm" title="Gift Cards" intro="A thoughtful gift for someone who deserves a moment to themselves." />

      <section className="bg-cream py-24">
        <Container className="max-w-3xl">
          {step === 'done' ? (
            <div className="border border-gold/50 bg-paper p-10 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold/20 text-gold-deep"><Gift size={30} /></div>
              <h2 className="mt-6 font-display text-3xl text-espresso">Request received</h2>
              <div className="rule-gold mx-auto mt-4" />
              <p className="mx-auto mt-5 max-w-md font-body text-muted">
                Thank you, {fromName.split(' ')[0] || 'and enjoy gifting'}. Once we confirm your payment, your {formatPula(finalAmount)} voucher
                {recipientName ? ` for ${recipientName}` : ''} will be activated.
              </p>
              <div className="mx-auto mt-6 flex max-w-md flex-col gap-2.5 text-left">
                <p className="flex items-start gap-2 font-body text-sm text-muted"><Clock size={16} className="mt-0.5 shrink-0 text-gold-deep" /> Activation confirmation is sent within about 2 hours during opening hours.</p>
                <p className="flex items-start gap-2 font-body text-sm text-muted"><ShieldCheck size={16} className="mt-0.5 shrink-0 text-gold-deep" /> Please keep your proof of payment — you may be asked to show it.</p>
              </div>
            </div>
          ) : (
            <div className="border border-line bg-paper p-7 sm:p-10">
              <p className="eyebrow mb-3">Choose an amount</p>
              <div className="flex flex-wrap gap-2.5">
                {TIERS.map((t) => (
                  <button key={t} onClick={() => { setAmount(t); setCustom(''); }}
                    className={`chip ${!custom && amount === t ? 'border-gold bg-gold/15 text-espresso' : 'border-line bg-white text-muted hover:border-gold'}`}>
                    {formatPula(t)}
                  </button>
                ))}
                <input className="field w-32" type="number" min={50} placeholder="Custom" value={custom} onChange={(e) => setCustom(e.target.value)} />
              </div>

              <div className="mt-7 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Recipient's name <span className="text-blush-deep">*</span></label>
                  <input className="field" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Naledi" />
                </div>
                <div>
                  <label className="label">Recipient's email (optional)</label>
                  <input className="field" type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="naledi@email.com" />
                </div>
                <div>
                  <label className="label">Your name <span className="text-blush-deep">*</span></label>
                  <input className="field" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="From…" />
                </div>
                <div>
                  <label className="label">Your email <span className="text-blush-deep">*</span></label>
                  <input className="field" type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} placeholder="you@email.com" />
                </div>
                <div>
                  <label className="label">Your phone <span className="text-blush-deep">*</span></label>
                  <input className="field" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} placeholder="+267 … (required)" />
                </div>
              </div>
              <div className="mt-4">
                <label className="label">Personal message (optional)</label>
                <textarea rows={3} className="field" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="A little note for the card…" />
              </div>
              <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden className="absolute left-[-9999px] h-0 w-0 opacity-0" />

              {error && (
                <div className="mt-5 rounded-md border border-blush-deep/40 bg-blush-soft/40 p-4">
                  <p className="font-body text-sm text-espresso">{error}</p>
                  <a href={waLink(biz.whatsapp, waText)} target="_blank" rel="noreferrer" className="btn-gold mt-3 !py-2.5 !text-[11px]"><MessageCircle size={14} /> Order on WhatsApp</a>
                </div>
              )}

              <div className="mt-7 flex items-center justify-between">
                <span className="font-display text-2xl text-espresso">{formatPula(finalAmount)}</span>
                <button onClick={toPayment} className="btn-primary">Continue to payment <ArrowRight size={16} /></button>
              </div>
              <p className="mt-4 flex items-center gap-1.5 font-body text-xs text-muted"><Check size={13} className="text-gold-deep" /> Pay by transfer or mobile money — we'll activate the voucher once payment is confirmed.</p>
            </div>
          )}
        </Container>
      </section>

      {/* Payment step */}
      {step === 'pay' && (
        <Modal size="lg" onClose={() => setStep('form')}>
          <h2 className="font-display text-2xl text-espresso">Pay {formatPula(finalAmount)}</h2>
          <p className="mt-1 font-body text-sm text-muted">Pay with any option below, then submit your request. Use <strong className="text-espresso">{ref}</strong> as the reference.</p>
          <div className="rule-gold mt-3" />

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <PayRow icon={Smartphone} label="FNB Pay to Cell" lines={[pay.payToCell]} />
            <PayRow icon={Smartphone} label="Orange Money" lines={[pay.orangeMoney]} />
            <PayRow icon={Smartphone} label="MyZaka" lines={[pay.myzaka]} />
            <PayRow icon={Landmark} label="Bank transfer" lines={[pay.bankName, `${pay.accountName}`, `Acc ${pay.accountNumber}`, `Branch ${pay.branchCode}`]} />
          </div>

          <div className="mt-5 rounded-md bg-cream p-4">
            <p className="flex items-start gap-2 font-body text-sm text-espresso"><Clock size={16} className="mt-0.5 shrink-0 text-gold-deep" /> Your card is activated within about <strong>2 hours</strong> of us confirming payment, during opening hours.</p>
            <p className="mt-2 flex items-start gap-2 font-body text-sm text-espresso"><ShieldCheck size={16} className="mt-0.5 shrink-0 text-gold-deep" /> Please keep your proof of payment — you may be asked to send or show it before the card is made active.</p>
          </div>

          {error && <p className="mt-4 font-body text-sm text-blush-deep">{error}</p>}

          <div className="mt-6 flex flex-col gap-2.5 sm:flex-row">
            <button onClick={() => setStep('form')} className="btn-outline flex-1 justify-center">Back</button>
            <button onClick={submit} className="btn-primary flex-1 justify-center">I've made payment <ArrowRight size={15} /></button>
          </div>
        </Modal>
      )}

      {/* Loading */}
      {step === 'loading' && (
        <Modal>
          <div className="py-6 text-center">
            <Loader2 size={40} className="mx-auto animate-spin text-gold-deep" />
            <p className="mt-5 font-display text-xl text-espresso">Submitting your request…</p>
            <p className="mt-2 font-body text-sm text-muted">Just a moment.</p>
          </div>
        </Modal>
      )}
    </>
  );
}
